import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";
import { Link, Navigate } from "react-router-dom";
import {
  Badge,
  Button,
  Checkbox,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import {
  CustomerTilesNoTitlesMessageStyles,
  CustomerTilesPanelStyles,
} from "./CustomerTilesPanelStyles";
import {
  BREAKPOINT_MOBILE,
  BREAKPOINT_DESKTOP_XL,
  MAGIC_STRING,
} from "constants/constants";
import { usePopulateCustomerJsonsInAtom } from "./CustomerTilesInternalTools/CustomerInfoIndicator";
import {
  FetchAllFlightConfigs,
  allFlightConfigsAtom,
} from "./CustomerTilesInternalTools/FetchAllFlightConfigs";
import { CustomerListItem, WhichConfigsType } from "./customerTilesUtils";
import {
  useDoesUserHaveRavenEmail,
  useIsAdmin,
} from "utils/customHooks/genericHooks";
import { SearchCustomers } from "./SearchCustomers";
import { SingleCustomerTileStyles } from "./SingleCustomerTileStyles";
import { useIsDarkMode } from "store/atoms";
import ServiceOutageIndicator from "components/SimpleComponents/TopBanner/ServiceOutageIndicator";
import { css } from "styled-components/macro";
import { useLocalStorage } from "react-use";
import { MoreVert } from "@mui/icons-material";
import { useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { createGlobalStyle } from "styled-components/macro";
import styled from "styled-components/macro";
import { getTokenInfo } from "contexts/AuthManager/Auth";
import { decodeToken } from "contexts/AuthManager/jwtHelper";
import { isEqual } from "lodash";
import { useAuth0 } from "../../../../contexts/AuthManager/Auth";
import { useCustomersList } from "pages/CustomerTilesPage/useCustomersQuery";
import { gql, useQuery } from "@apollo/client";
import { machemetryClient } from "providers/MachemetryProvider";
import useFlagsWithDefaults from "utils/customHooks/useFlagsWithDefaults";
import { customerInfoJsonAtom } from "reports/utils/hooks/useCustomerInfoJson";
import { getTimeZoneOffsetMsFromLocaleString } from "utils/timeZoneHooks";
import { highlight } from "constants/colors";
import {
  customerTileConfigAtom,
  CustomerTileMoreInfo,
} from "./CustomerTilesInternalTools/CustomerTileMoreInfo";
import {
  FetchAllWorkScheduleConfigs,
  allWorkSchedulesAtom,
} from "./CustomerTilesInternalTools/FetchAllWorkScheduleConfigs";
import { CustomerTilesInspectPopup } from "./CustomerTilesInternalTools/CustomerTilesInspectPopup";
import {
  FetchAllAskrConfigs,
  allAskrConfigsAtom,
} from "./CustomerTilesInternalTools/FetchAllAskrConfigs";
import { FetchAllMidgardProcessGroups } from "./CustomerTilesInternalTools/FetchAllMidgardProcessGroups";
export type CustomerTileProps = {
  customersList: CustomerListItem[];
};

const MACHEMETRY_TENANTS_QUERY = gql`
  query {
    rollups @rest(type: "Rollups", path: "tiles") {
      details {
        name
        link
        rollupShortName
        perf
      }
    }
  }
`;

const CustomerTilesPanel = ({ customersList }: CustomerTileProps) => {
  const { user, isAuthenticated } = useAuth0();

  const customersInTokenActual = useMemo(() => {
    const { idToken } = getTokenInfo();
    const decoded = idToken && decodeToken(idToken);
    return (
      user?.["https://raven.ai/app_metadata"]?.customer ??
      user?.app_metadata?.customer ??
      user?.customer ??
      decoded?.["https://raven.ai/app_metadata"]?.customer ??
      decoded?.app_metadata?.customer ??
      decoded?.customer ??
      []
    );
  }, [user]);
  const [customersInTokenLS, setCustomersInTokenLS] = useLocalStorage<string[]>(
    "CustomerTiles:customersInToken",
    []
  );
  useEffect(() => {
    if (!isEqual(customersInTokenActual, customersInTokenLS)) {
      setCustomersInTokenLS(customersInTokenActual);
    }
  }, [customersInTokenLS, customersInTokenActual, setCustomersInTokenLS]);

  const customersInToken = useMemo(
    () =>
      !customersInTokenActual || customersInTokenActual.length === 0
        ? customersInTokenLS
        : customersInTokenActual,
    [customersInTokenActual, customersInTokenLS]
  );

  // if user has "raven-employees" customer, show all customers instead
  const [, setCustomersList] = useCustomersList();

  const { data, loading, refetch, error } = useQuery(MACHEMETRY_TENANTS_QUERY, {
    client: machemetryClient,
    fetchPolicy: "network-only",
    skip: !isAuthenticated,
    onError: err => {
      // if we get an error, try again in 10 seconds
      setTimeout(refetch, 10 * 1000);
      console.log(`${err} - retrying in 10 seconds`);
    },
  });
  const isRavenEmailUser = useDoesUserHaveRavenEmail();
  const isAdminUser = useIsAdmin();
  const { flightMagicStringEnabled } = useFlagsWithDefaults();
  useEffect(() => {
    if (
      customersInToken.includes(MAGIC_STRING) &&
      isRavenEmailUser &&
      flightMagicStringEnabled
    ) {
      if (data?.rollups && !loading && !error) {
        const tenants: CustomerListItem[] = data.rollups.details?.map(t => ({
          abbrevName: t.name,
          customerName: t.rollupShortName,
        }));
        if (tenants) {
          const tenantKeys = tenants.map(t => t.abbrevName);
          window.localStorage.setItem(
            "raven_employee:ckeys",
            JSON.stringify(tenantKeys)
          );
          setCustomersInTokenLS(tenantKeys);
          setCustomersList(tenants);
        }
      }
    }
  }, [
    customersInToken,
    data?.rollups,
    error,
    loading,
    setCustomersInTokenLS,
    setCustomersList,
    isRavenEmailUser,
    flightMagicStringEnabled,
  ]);

  const [searchTerm, setSearchTerm] = useState("");
  const [searchConfigsTerm, setSearchConfigsTerm] = useState("");
  const [{ showMoreInfo }] = useAtom(customerTileConfigAtom);

  const handleSearchChange = useCallback(e => {
    setSearchTerm(e.target.value);
  }, []);

  const handleSearchConfigsChange = useCallback((searchTerm: string) => {
    setSearchConfigsTerm(searchTerm);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchTerm("");
    setSearchConfigsTerm("");
  }, []);
  const [allFlightConfigs] = useAtom(allFlightConfigsAtom);

  const initialCustomers = useMemo(
    () =>
      customersInToken.includes(MAGIC_STRING) && isRavenEmailUser
        ? customersList
        : customersInToken.map(ckey => ({
            abbrevName: ckey,
            customerName: customersList.find(
              ({ abbrevName }) => abbrevName === ckey
            )?.customerName,
          })),
    [customersInToken, customersList, isRavenEmailUser]
  );

  const [customersFiltered, setCustomersFiltered] = useState(initialCustomers);
  const [isPending, startTransition] = useTransition();
  const [allCustomerInfo] = useAtom(customerInfoJsonAtom);
  const [allAskrConfigs] = useAtom(allAskrConfigsAtom);
  const [allWorkSchedulesConfigs] = useAtom(allWorkSchedulesAtom);
  const [whichConfigsToSearch, setWhichConfigsToSearch] = useState<
    WhichConfigsType[]
  >(["flightConfig"]);
  useEffect(() => {
    const searchConfigsTermLower = searchConfigsTerm?.toLowerCase().trim();
    if (!searchTerm && !searchConfigsTermLower) {
      if (customersFiltered.length !== initialCustomers.length) {
        setCustomersFiltered(initialCustomers);
      }
      return;
    }
    function filterCustomers() {
      const filtered = initialCustomers.filter(
        ({ abbrevName: ckey, customerName }) => {
          let matchesSearchTerm = true;
          if (searchTerm) {
            matchesSearchTerm = `${ckey}${customerName}`
              .toLowerCase()
              .includes(searchTerm.toLowerCase());
          }

          let matchesConfigSearchTerm = !searchConfigsTermLower;

          const flightConfig = allFlightConfigs[ckey];
          const searchingFlightConfig =
            whichConfigsToSearch.includes("flightConfig");
          if (searchConfigsTermLower && searchingFlightConfig) {
            matchesConfigSearchTerm =
              matchesConfigSearchTerm ||
              Object.values(flightConfig ?? {})
                .join(";")
                .replaceAll("\\", "")
                .toLowerCase()
                .includes(searchConfigsTermLower);
          }

          const customerInfo = allCustomerInfo[ckey];
          const searchingCustomerInfo =
            whichConfigsToSearch.includes("customerInfo");
          if (searchConfigsTermLower && searchingCustomerInfo) {
            matchesConfigSearchTerm =
              matchesConfigSearchTerm ||
              JSON.stringify(customerInfo ?? {})
                .replaceAll("\\", "")
                .replaceAll(" ", "")
                .toLowerCase()
                .includes(searchConfigsTermLower.replaceAll(" ", ""));
          }

          const workSchedulesConfig = allWorkSchedulesConfigs[ckey];
          const searchingWorkSchedules =
            whichConfigsToSearch.includes("workSchedules");
          if (searchConfigsTermLower && searchingWorkSchedules) {
            matchesConfigSearchTerm =
              matchesConfigSearchTerm ||
              JSON.stringify(workSchedulesConfig ?? {})
                .replaceAll("\\", "")
                .replaceAll(" ", "")
                .toLowerCase()
                .includes(searchConfigsTermLower.replaceAll(" ", ""));
          }

          const askrConfig = allAskrConfigs[ckey];
          const searchingAskr = whichConfigsToSearch.includes("askrConfig");
          if (searchConfigsTermLower && searchingAskr) {
            matchesConfigSearchTerm =
              matchesConfigSearchTerm ||
              JSON.stringify(askrConfig ?? {})
                .replaceAll("\\", "")
                .toLowerCase()
                .includes(searchConfigsTermLower);
          }

          return matchesSearchTerm && matchesConfigSearchTerm;
        }
      );
      setCustomersFiltered(filtered);
    }
    startTransition(() => {
      filterCustomers();
    });
  }, [
    allFlightConfigs,
    searchTerm,
    searchConfigsTerm,
    initialCustomers,
    allCustomerInfo,
    allAskrConfigs,
    allWorkSchedulesConfigs,
    whichConfigsToSearch,
    customersFiltered.length,
  ]);

  const onlyCustomerInfo =
    customersInToken.length === 1
      ? customersList.find(
          ({ abbrevName }) => abbrevName === customersInToken[0]
        )
      : null;
  if (customersInToken.length === 0) {
    return (
      <CustomerTilesNoTitlesMessageStyles>
        Oh no, it looks like you don{`'`}t have any tiles available!
      </CustomerTilesNoTitlesMessageStyles>
    );
  } else if (customersInToken.length === 1 && onlyCustomerInfo) {
    return (
      <Navigate
        replace={true}
        to={`/${onlyCustomerInfo.abbrevName}/dashboard`}
      />
    );
  }

  const shouldShowSearch = customersList.length > 8;
  return (
    <div data-component-name={"Customer Tiles Page"}>
      <ServiceOutageIndicator
        customCss={css`
          margin: 5px 5px 0;
          @media (min-width: ${BREAKPOINT_MOBILE}px) {
            margin: 10px 20px;
          }
          position: fixed;
          z-index: 1000;
          bottom: 8px;
          opacity: 0.9;
          box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.2);
          left: 0;
          right: 0;
        `}
      />
      {shouldShowSearch ? (
        <SearchCustomers
          {...{
            searchTerm,
            handleSearchChange,
            handleClearSearch,
            showMoreInfo,
            searchConfigsTerm,
            handleSearchConfigsChange,
            whichConfigsToSearch,
            setWhichConfigsToSearch,
          }}
        />
      ) : null}
      {isAdminUser && <CustomerTilesConfigMenu />}
      {showMoreInfo && (
        <>
          <FetchMoreInfo {...{ customersList }} />
        </>
      )}
      <CustomerTilesPanelStyles
        data-component-name={"Customer Tiles Panel"}
        $shouldShowSearch={shouldShowSearch}
        $isPending={isPending}
      >
        {customersFiltered.map(({ abbrevName: ckey, customerName }) => {
          return (
            <CustomerTileContent
              key={ckey}
              {...{
                customerName,
                ckey,
              }}
            />
          );
        })}
      </CustomerTilesPanelStyles>
      <CustomerTilesInspectPopup />
    </div>
  );
};

export default CustomerTilesPanel;

function FetchMoreInfo({
  customersList,
}: {
  customersList: CustomerListItem[];
}) {
  usePopulateCustomerJsonsInAtom(customersList);
  return (
    <>
      <FetchAllFlightConfigs {...{ customersList }} />
      <FetchAllMidgardProcessGroups {...{ customersList }} />
      <FetchAllWorkScheduleConfigs {...{ customersList }} />
      <FetchAllAskrConfigs {...{ customersList }} />
    </>
  );
}

const lastSelectedCkeyAtom = atomWithStorage(
  "customerTiles:lastSelectedCkey",
  ""
);
function CustomerTileContent({
  ckey,
  customerName,
}: {
  customerName?: string;
  ckey: string;
}) {
  const customerJson = useAtom(customerInfoJsonAtom)[0][ckey];
  const { tzUtcOffset } = getTimeZoneOffsetMsFromLocaleString(
    customerJson?.timeZone
  );
  const timezoneReadable = customerJson?.timeZone
    ? `${customerJson?.timeZone} (UTC ${tzUtcOffset})`
    : "";

  const isAdminUser = useIsAdmin();
  const [isDarkMode] = useIsDarkMode();
  const [lastSelectedCkey, setLastSelectedCkey] = useAtom(lastSelectedCkeyAtom);
  const isLastSelected = lastSelectedCkey === ckey;

  const [{ showMoreInfo }] = useAtom(customerTileConfigAtom);
  const [hovered, setHovered] = useState(false);
  const [clicked, setClicked] = useState(false);

  const [allWorkSchedules] = useAtom(allWorkSchedulesAtom);
  const workSchedulesConfigList = allWorkSchedules[ckey];

  return (
    <>
      <Tooltip
        disableInteractive={true}
        title={
          isAdminUser && showMoreInfo ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              timezone: {timezoneReadable}
            </div>
          ) : null
        }
      >
        <SingleCustomerTileStyles
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => {
            setHovered(false);
            setClicked(false);
          }}
          className="customerTileStyles"
          key={ckey}
          $isDarkMode={isDarkMode}
          $isLastSelected={isLastSelected}
        >
          <Link
            className={`linkToCustomer customer-${ckey}`}
            to={`/${ckey}/schedule`}
            style={{ textDecoration: "none" }}
            onContextMenu={e => {
              if (isAdminUser) {
                setLastSelectedCkey(ckey);
              }
            }}
          >
            <Button
              className="customerTileButton"
              onClick={() => {
                setLastSelectedCkey(ckey);
              }}
            >
              {showMoreInfo && workSchedulesConfigList?.length > 1 ? (
                <div className="multi-work-schedules-indicator" />
              ) : null}
              <SingleCustomerTile
                name={customerName || ckey || ""}
                ckey={ckey}
              />
            </Button>
          </Link>
          {showMoreInfo && (
            <CustomerTileMoreInfo
              {...{ ckey, hovered, setHovered, clicked, setClicked }}
            />
          )}
        </SingleCustomerTileStyles>
      </Tooltip>
    </>
  );
}

function CustomerTilesConfigMenu() {
  const [open, setOpen] = useState(false);

  const [
    {
      showMoreInfo,
      dimDisabledCustomers,
      showAskrConfigs,
      showGroupsAndProcesses,
    },
    setCustomerTileConfig,
  ] = useAtom(customerTileConfigAtom);

  const ref = useRef<HTMLButtonElement | null>(null);
  const [isNew, setNew] = useLocalStorage(
    "isNew:customerTilesConfigMenu",
    true
  );
  return (
    <>
      <CustomerTilesConfigMenuStyles>
        <Tooltip title={`More customer details`} disableInteractive={true}>
          <IconButton ref={ref} size="small" onClick={() => setOpen(!open)}>
            <Badge
              componentsProps={{
                badge: {
                  style: {
                    backgroundColor: highlight,
                    color: "white",
                    top: 0,
                    right: 0,
                    transform: `scale(${isNew ? 0.5 : 0}) translate(50%, -50%)`,
                  },
                },
              }}
              badgeContent=""
            >
              <MoreVert />
            </Badge>
          </IconButton>
        </Tooltip>
        <Menu
          disableScrollLock={true}
          open={open}
          onClose={() => setOpen(false)}
          anchorReference="anchorPosition"
          anchorPosition={
            open && ref.current
              ? {
                  top: ref.current.getBoundingClientRect().top,
                  left: ref.current.getBoundingClientRect().left,
                }
              : undefined
          }
        >
          <MenuItem
            onClick={() => {
              setCustomerTileConfig(prev => ({
                ...prev,
                showMoreInfo: !showMoreInfo,
              }));
              setNew(false);
            }}
          >
            <Badge
              componentsProps={{
                badge: {
                  style: {
                    backgroundColor: highlight,
                    color: "white",
                    inset: "11px auto 0 -28px",
                    transform: `scale(${isNew ? 0.4 : 0}) translate(50%, -50%)`,
                    transition: "transform 0.2s ease-in-out",
                  },
                },
              }}
              badgeContent=""
            >
              <div>{showMoreInfo ? "Hide" : "Show"} more customer details</div>
            </Badge>
          </MenuItem>
          {showMoreInfo
            ? [
                <MenuItem
                  key={0}
                  onClick={() =>
                    setCustomerTileConfig(prev => ({
                      ...prev,
                      showAskrConfigs: !prev.showAskrConfigs,
                    }))
                  }
                  style={{ marginLeft: 12 }}
                >
                  <Checkbox checked={showAskrConfigs} /> Askr configs
                </MenuItem>,
                <MenuItem
                  key={1}
                  onClick={() =>
                    setCustomerTileConfig(prev => ({
                      ...prev,
                      showGroupsAndProcesses: !prev.showGroupsAndProcesses,
                    }))
                  }
                  style={{ marginLeft: 12 }}
                >
                  <Checkbox checked={showGroupsAndProcesses} /> Groups and
                  Processes
                </MenuItem>,
              ]
            : null}
          <MenuItem
            onClick={() =>
              setCustomerTileConfig(prev => ({
                ...prev,
                dimDisabledCustomers: !prev.dimDisabledCustomers,
              }))
            }
          >
            {dimDisabledCustomers ? "Un-dim" : "Dim"} disabled customers
          </MenuItem>
        </Menu>
      </CustomerTilesConfigMenuStyles>
      {dimDisabledCustomers && <DimDeactivatedCustomersGlobalStyles />}
    </>
  );
}
const CustomerTilesConfigMenuStyles = styled.div`
  position: absolute;
  top: 74px;
  right: 20px;
  @media (min-width: ${BREAKPOINT_DESKTOP_XL}px) {
    top: 108px;
  }
`;
/** customers no longer with us whose tiles remain,
 * or demo/test customers without real data
 */
const DEACTIVATED_CUSTOMERS = [
  "bcg-demo",
  "ravendemo",
  "raveng",
  "ravenjest",
  "ravenjest2",
  "ravenjest3",
  "ravenmanufacturing",
  "ravenstaging0",
  "silvercote",
  "silvercote-brampton",
  "silvercote-dallas",
  "silvercote-denver",
  "silvercote-duluth",
  "silvercote-portland",
  "silvercote-siouxfalls",
  "silvercote-slc",
  "silvercote-southcarolina",
  "silvercote-springfield",
  "silvercote-stockton",
  "silvercote-wrightcity",
  "silvercote-valdosta",
  "sojo-bristol",
  "sojo-cedarlane",
  "sojo-denver",
  "sojo-indianapolis",
  "sojo-nevada",
];

const DimDeactivatedCustomersGlobalStyles = createGlobalStyle`
 ${DEACTIVATED_CUSTOMERS.reduce((acc, cur) => {
   return `${acc}${acc.length ? "," : ""} .linkToCustomer.customer-${cur}`;
 }, "")} {opacity:0.5}
`;

function SingleCustomerTile({ name, ckey }: { name: string; ckey: string }) {
  return (
    <div className="singleCustomerTile" data-component-name={"Customer Tile"}>
      <div className="top-group-title">
        {name?.toLowerCase() === "ravenjest"
          ? "stable customer for flight e2e tests"
          : name}
      </div>
      <div className="top-group-subtitle">flight.raven.ai/{ckey}</div>
    </div>
  );
}
